import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useMobileContext } from '../components/Layout';
import { MobileHeader } from '../components/mobile';
import BuildingBanner from '../components/BuildingBanner';
import { Send, Plus, Edit2, Trash2, X, Check, Building, Pin, PinOff, MoreVertical, ArrowLeft, MessageSquare } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Messagerie = () => {
  const { user, token } = useAuth();
  const { isMobile, onMenuClick } = useMobileContext();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showNewChat, setShowNewChat] = useState(false);
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [users, setUsers] = useState([]);
  const [newChatRecipient, setNewChatRecipient] = useState('');
  const [newChatBuilding, setNewChatBuilding] = useState('');
  const [showConvMenu, setShowConvMenu] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  // Polling pour mise à jour en temps réel des conversations
  useEffect(() => {
    if (!token) return;
    
    const pollInterval = setInterval(() => {
      fetchConversationsQuietly();
    }, 5000); // Rafraîchir la liste toutes les 5 secondes
    
    return () => clearInterval(pollInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (selectedConv && token) {
      fetchMessages(selectedConv.id);
    }
  }, [selectedConv, token]);

  // Polling pour mise à jour en temps réel des messages
  useEffect(() => {
    if (!selectedConv || !token) return;
    
    const pollInterval = setInterval(() => {
      fetchMessagesQuietly(selectedConv.id);
    }, 3000); // Rafraîchir les messages toutes les 3 secondes
    
    return () => clearInterval(pollInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConv, token]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch silencieux (sans loading state) pour le polling
  const fetchConversationsQuietly = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/conversations`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setConversations(response.data);
    } catch (error) {
      // Silencieux - pas d'erreur affichée pour le polling
    }
  };

  const fetchMessagesQuietly = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Seulement mettre à jour si le nombre de messages a changé
      if (response.data.length !== messages.length) {
        setMessages(response.data);
      }
    } catch (error) {
      // Silencieux
    }
  };

  const fetchData = async () => {
    if (!token) return;
    
    try {
      const [convsRes, buildingsRes, usersRes] = await Promise.all([
        axios.get(`${API}/conversations`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/immeubles`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      
      setConversations(convsRes.data);
      setBuildings(buildingsRes.data);
      setUsers(usersRes.data.filter(u => u.id !== user?.id));
      
      if (convsRes.data.length > 0 && !selectedConv) {
        setSelectedConv(convsRes.data[0]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConv) return;

    const tempMessage = {
      id: `temp-${Date.now()}`,
      content: newMessage,
      sender_id: user?.id,
      sender: { prenom: user?.prenom, nom: user?.nom },
      created_at: new Date().toISOString(),
      pending: true
    };

    // Ajouter immédiatement le message à l'interface
    setMessages(prev => [...prev, tempMessage]);
    setNewMessage('');

    try {
      await axios.post(
        `${API}/conversations/${selectedConv.id}/messages`,
        { content: tempMessage.content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Rafraîchir pour avoir la vraie version du message
      fetchMessages(selectedConv.id);
      fetchConversationsQuietly();
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleEditMessage = async (messageId) => {
    if (!editContent.trim()) return;
    
    try {
      await axios.put(
        `${API}/messages/${messageId}`,
        { content: editContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingMessageId(null);
      setEditContent('');
      fetchMessages(selectedConv.id);
    } catch (error) {
      console.error('Error editing message:', error);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) return;
    
    try {
      await axios.delete(`${API}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMessages(selectedConv.id);
      fetchData();
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const startEditing = (msg) => {
    setEditingMessageId(msg.id);
    setEditContent(msg.content);
  };

  const cancelEditing = () => {
    setEditingMessageId(null);
    setEditContent('');
  };

  const handleCreateConversation = async (e) => {
    e.preventDefault();
    if (!newChatRecipient || !newChatBuilding) return;
    
    try {
      const response = await axios.post(
        `${API}/conversations`,
        {
          participant_ids: [newChatRecipient],
          building_id: newChatBuilding,
          is_group: false
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowNewChat(false);
      setNewChatRecipient('');
      setNewChatBuilding('');
      await fetchData();
      
      // Select the new conversation
      const newConvId = response.data.conversation_id;
      const convs = await axios.get(`${API}/conversations`, { headers: { Authorization: `Bearer ${token}` } });
      const newConv = convs.data.find(c => c.id === newConvId);
      if (newConv) setSelectedConv(newConv);
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  };

  const handlePinConversation = async (convId, isPinned) => {
    try {
      const endpoint = isPinned ? 'unpin' : 'pin';
      await axios.put(
        `${API}/conversations/${convId}/${endpoint}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowConvMenu(null);
      fetchData();
    } catch (error) {
      console.error('Error toggling pin:', error);
    }
  };

  const handleDeleteConversation = async (convId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette conversation ? Tous les messages seront perdus.')) return;
    
    try {
      await axios.delete(`${API}/conversations/${convId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowConvMenu(null);
      if (selectedConv?.id === convId) {
        setSelectedConv(null);
      }
      fetchData();
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const getInitials = (nom, prenom) => {
    const first = prenom?.[0]?.toUpperCase() || '';
    const last = nom?.[0]?.toUpperCase() || '';
    return `${first}${last}` || '?';
  };

  const getConvName = (conv) => {
    if (conv.name) return conv.name;
    if (conv.participants?.length > 0) {
      return conv.participants.map(p => `${p.prenom} ${p.nom}`).join(', ');
    }
    return 'Conversation';
  };

  const getBuildingDisplayName = (building) => {
    if (!building) return '';
    const nom = building.nom || '';
    const adresse = building.adresse || '';
    const codePostal = building.code_postal || '';
    const ville = building.ville || '';
    
    // Format: "Nom - Adresse, Code Postal Ville"
    let display = nom;
    if (adresse) {
      display += display ? ` - ${adresse}` : adresse;
    }
    if (codePostal || ville) {
      display += `, ${codePostal} ${ville}`.trim();
    }
    return display;
  };

  const getBuildingName = (buildingId) => {
    const building = buildings.find(b => b.id === buildingId);
    return getBuildingDisplayName(building);
  };

  const filteredConversations = selectedBuilding 
    ? conversations.filter(c => c.building_id === selectedBuilding)
    : conversations;

  if (loading) {
    return (
      <>
        {isMobile && (
          <MobileHeader 
            title="Messagerie"
            onMenuClick={onMenuClick}
          />
        )}
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8]"></div></div>
      </>
    );
  }

  return (
    <>
      {/* Mobile Header */}
      {isMobile && (
        <MobileHeader 
          title={selectedConv ? getConvName(selectedConv) : 'Messagerie'}
          onMenuClick={onMenuClick}
          showBackButton={!!selectedConv}
          rightAction={selectedConv ? (
            <button
              onClick={() => setSelectedConv(null)}
              className="p-2 -mr-2 text-white hover:bg-white/10 rounded-full transition-colors"
            >
              <X size={24} />
            </button>
          ) : undefined}
        />
      )}

    <div className={`p-6 max-w-7xl mx-auto ${isMobile ? 'p-4' : ''}`} data-testid="messagerie-page">
      {/* Building Banner for Copropriétaires */}
      {user?.role === 'coproprietaire' && (
        <BuildingBanner token={token} user={user} compact={true} />
      )}
      
      {!isMobile && (
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-[#00D3A8]/10 rounded-xl">
            <MessageSquare className="h-8 w-8 text-[#00D3A8]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Messagerie</h1>
            <p className="text-gray-500">Communiquez avec vos coproprietaires et prestataires</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-100 overflow-hidden" style={{ height: '70vh' }}>
        <div className="flex flex-col lg:grid lg:grid-cols-3 h-full">
          {/* Liste des conversations - Hidden on mobile when conversation selected */}
          <div className={`${selectedConv ? 'hidden lg:flex' : 'flex'} lg:col-span-1 border-r border-gray-200 flex-col h-full overflow-hidden`}>
            <div className="p-3 md:p-4 border-b border-gray-200 space-y-2 md:space-y-3 flex-shrink-0">
              {/* Sélecteur d'immeuble */}
              <div className="relative">
                <Building size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <select 
                  value={selectedBuilding}
                  onChange={(e) => setSelectedBuilding(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] outline-none"
                  data-testid="building-filter"
                >
                  <option value="">Tous les immeubles</option>
                  {buildings.map(b => (
                    <option key={b.id} value={b.id}>{getBuildingDisplayName(b)}</option>
                  ))}
                </select>
              </div>
              
              <button
                onClick={() => setShowNewChat(true)}
                className="w-full bg-gradient-to-r from-[#00D3A8] to-[#00C7A1] hover:opacity-90 text-white py-2 px-4 rounded-full transition-all flex items-center justify-center space-x-2 font-medium shadow-md text-sm md:text-base"
                data-testid="new-chat-btn"
              >
                <Plus size={20} />
                <span>Nouveau message</span>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto min-h-0">
              {filteredConversations.map((conv) => {
                const participant = conv.participants?.[0];
                const participantPhoto = participant?.photo_url;
                const participantInitials = conv.is_group ? 'G' : getInitials(participant?.nom, participant?.prenom);
                
                return (
                  <div
                    key={conv.id}
                    className={`relative p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedConv?.id === conv.id ? 'bg-[#00D3A8]/5 border-l-4 border-l-[#00D3A8]' : ''
                    }`}
                    data-testid={`conversation-${conv.id}`}
                  >
                    <div className="flex items-start space-x-3" onClick={() => setSelectedConv(conv)}>
                      <div className="relative flex-shrink-0">
                        {participantPhoto ? (
                          <img 
                            src={participantPhoto} 
                            alt={getConvName(conv)}
                            className="w-11 h-11 rounded-full object-cover border-2 border-white shadow-sm"
                          />
                        ) : (
                          <div className="w-11 h-11 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-sm">
                            {participantInitials}
                          </div>
                        )}
                        {conv.pinned && (
                          <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-400 rounded-full flex items-center justify-center shadow">
                            <Pin size={10} className="text-white" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-800 truncate">{getConvName(conv)}</div>
                        <div className="text-xs text-gray-400 truncate">{getBuildingName(conv.building_id)}</div>
                        <div className="text-sm text-gray-500 truncate mt-0.5">
                          {conv.last_message?.content || 'Aucun message'}
                        </div>
                      </div>
                    </div>
                    
                    {/* Menu bouton */}
                    <div className="absolute top-2 right-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowConvMenu(showConvMenu === conv.id ? null : conv.id); }}
                        className="p-1.5 rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600"
                        data-testid={`conv-menu-btn-${conv.id}`}
                      >
                        <MoreVertical size={16} />
                      </button>
                      
                      {/* Dropdown menu */}
                      {showConvMenu === conv.id && (
                        <div className="absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-44 z-10">
                          <button
                            onClick={(e) => { e.stopPropagation(); handlePinConversation(conv.id, conv.pinned); }}
                            className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          data-testid={`pin-conv-${conv.id}`}
                        >
                          {conv.pinned ? (
                            <>
                              <PinOff size={16} />
                              <span>Désépingler</span>
                            </>
                          ) : (
                            <>
                              <Pin size={16} />
                              <span>Épingler</span>
                            </>
                          )}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteConversation(conv.id); }}
                          className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                          data-testid={`delete-conv-${conv.id}`}
                        >
                          <Trash2 size={16} />
                          <span>Supprimer</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
              })}

              {filteredConversations.length === 0 && (
                <div className="p-4 text-center text-gray-500">
                  Aucune conversation
                </div>
              )}
            </div>
          </div>

          {/* Zone de chat */}
          <div className={`${selectedConv ? 'flex' : 'hidden lg:flex'} lg:col-span-2 flex-col h-full overflow-hidden`}>
            {selectedConv ? (
              <>
                {/* Header */}
                <div className="p-3 md:p-4 border-b border-gray-200 bg-gray-50/50 flex-shrink-0">
                  {/* Mobile back button */}
                  <button
                    onClick={() => setSelectedConv(null)}
                    className="lg:hidden flex items-center text-[#00D3A8] mb-2 text-sm font-medium"
                  >
                    ← Retour aux conversations
                  </button>
                  <div className="font-semibold text-gray-800">{getConvName(selectedConv)}</div>
                  <div className="text-xs text-gray-400 flex items-center mt-1">
                    <Building size={12} className="mr-1" />
                    {getBuildingName(selectedConv.building_id)}
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-4 bg-gray-50/30 min-h-0">
                  {messages.map((msg, index) => {
                    const isCurrentUser = msg.sender_id === user?.id;
                    const isEditing = editingMessageId === msg.id;
                    const senderName = msg.sender ? `${msg.sender.prenom || ''} ${msg.sender.nom || ''}`.trim() : 'Utilisateur';
                    const senderPhoto = msg.sender?.photo_url;
                    const senderInitials = getInitials(msg.sender?.nom, msg.sender?.prenom);
                    
                    // Show sender name if it's a different sender than previous message
                    const prevMsg = messages[index - 1];
                    const showSenderName = !isCurrentUser && (!prevMsg || prevMsg.sender_id !== msg.sender_id);
                    
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                        data-testid={`message-${msg.id}`}
                      >
                        <div className={`flex items-end space-x-2 max-w-[85%] md:max-w-[70%] ${isCurrentUser ? 'flex-row-reverse space-x-reverse' : ''}`}>
                          {/* Avatar */}
                          <div className="flex-shrink-0 mb-1">
                            {senderPhoto ? (
                              <img 
                                src={senderPhoto} 
                                alt={senderName}
                                className="w-8 h-8 md:w-10 md:h-10 rounded-full object-cover border-2 border-white shadow-sm"
                              />
                            ) : (
                              <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-white font-semibold text-xs md:text-sm shadow-sm ${
                                isCurrentUser 
                                  ? 'bg-gradient-to-br from-[#00D3A8] to-[#00C7A1]' 
                                  : 'bg-gradient-to-br from-blue-500 to-blue-600'
                              }`}>
                                {senderInitials || '?'}
                              </div>
                            )}
                          </div>
                          
                          {/* Message content */}
                          <div className={`flex flex-col ${isCurrentUser ? 'items-end' : 'items-start'}`}>
                            {/* Sender name (only for received messages) */}
                            {showSenderName && (
                              <span className="text-xs text-gray-500 mb-1 ml-1 font-medium">
                                {senderName}
                              </span>
                            )}
                            
                            {isEditing ? (
                              <div className="flex items-center space-x-2">
                                <input
                                  type="text"
                                  value={editContent}
                                  onChange={(e) => setEditContent(e.target.value)}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] outline-none"
                                  autoFocus
                                  data-testid="edit-message-input"
                                />
                                <button
                                  onClick={() => handleEditMessage(msg.id)}
                                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                  data-testid="confirm-edit-btn"
                                >
                                  <Check size={18} />
                                </button>
                                <button
                                  onClick={cancelEditing}
                                  className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                                  data-testid="cancel-edit-btn"
                                >
                                  <X size={18} />
                                </button>
                              </div>
                            ) : (
                              <div className="group relative">
                                <div className={`px-4 py-2.5 rounded-2xl shadow-sm ${
                                  isCurrentUser 
                                    ? 'bg-gradient-to-r from-[#00D3A8] to-[#00C7A1] text-white rounded-br-md' 
                                    : 'bg-white text-gray-800 border border-gray-100 rounded-bl-md'
                                }`}>
                                  <p className="text-sm md:text-base leading-relaxed">{msg.content}</p>
                                  {msg.edited && (
                                    <span className={`text-xs ${isCurrentUser ? 'text-white/70' : 'text-gray-400'}`}> (modifié)</span>
                                  )}
                                </div>
                                
                                {/* Timestamp */}
                                <div className={`flex items-center space-x-1 mt-1 ${isCurrentUser ? 'justify-end' : 'justify-start'}`}>
                                  <span className="text-xs text-gray-400">
                                    {new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                                
                                {/* Actions d'édition/suppression */}
                                {isCurrentUser && (
                                  <div className={`absolute top-1/2 -translate-y-1/2 ${isCurrentUser ? 'left-0 -translate-x-full pr-2' : 'right-0 translate-x-full pl-2'} opacity-0 group-hover:opacity-100 transition-opacity flex items-center space-x-1 bg-white rounded-lg shadow-sm border border-gray-100 p-1`}>
                                    <button
                                      onClick={() => startEditing(msg)}
                                      className="p-1.5 text-gray-400 hover:text-[#00D3A8] hover:bg-gray-100 rounded transition-colors"
                                      title="Modifier"
                                      data-testid={`edit-msg-${msg.id}`}
                                    >
                                      <Edit2 size={14} />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteMessage(msg.id)}
                                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                      title="Supprimer"
                                      data-testid={`delete-msg-${msg.id}`}
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 bg-white flex-shrink-0">
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Écrire un message..."
                      className="flex-1 px-4 py-2.5 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all"
                      data-testid="message-input"
                    />
                    <button
                      type="submit"
                      className="p-2.5 bg-gradient-to-r from-[#00D3A8] to-[#00C7A1] hover:opacity-90 text-white rounded-full transition-all shadow-md"
                      data-testid="send-message-btn"
                    >
                      <Send size={20} />
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Send size={24} className="text-gray-300" />
                  </div>
                  <p>Sélectionnez une conversation</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal nouveau chat */}
      {showNewChat && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl" data-testid="new-chat-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold font-[Manrope] text-gray-800">Nouveau message</h2>
              <button onClick={() => setShowNewChat(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleCreateConversation} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Immeuble concerné <span className="text-red-500">*</span>
                </label>
                <select
                  value={newChatBuilding}
                  onChange={(e) => setNewChatBuilding(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] outline-none"
                  required
                  data-testid="new-chat-building"
                >
                  <option value="">Sélectionner un immeuble</option>
                  {buildings.map(b => (
                    <option key={b.id} value={b.id}>{getBuildingDisplayName(b)}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Destinataire <span className="text-red-500">*</span>
                </label>
                <select
                  value={newChatRecipient}
                  onChange={(e) => setNewChatRecipient(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] outline-none"
                  required
                  data-testid="new-chat-recipient"
                >
                  <option value="">Sélectionner un destinataire</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.prenom} {u.nom} ({u.role})
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowNewChat(false)}
                  className="px-5 py-2 border border-gray-200 rounded-full hover:bg-gray-50 transition-all font-medium"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-gradient-to-r from-[#00D3A8] to-[#00C7A1] text-white rounded-full hover:opacity-90 transition-all font-medium shadow-md"
                  data-testid="create-chat-btn"
                >
                  Démarrer la conversation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default Messagerie;
